import { z } from 'zod';
import { CREDIT_NOTE_STATUSES, CURRENCIES, PAYMENT_METHODS, } from '../enums.js';
import { optionalText, paginationSchema, patchSchemaOf } from './common.js';
/**
 * Return invoices — credit notes and refunds. DECISIONS.md §16.
 *
 * A credit note is raised against ONE invoice, from that invoice's own lines.
 * Only a line and a quantity come from the client: the description and the
 * unit price are copied from the invoice line on the server, so a credit note
 * can never price a line differently from the bill it reduces. The currency is
 * the invoice's, always, and is not accepted here at all.
 */
export const creditNoteLineInputSchema = z.object({
    invoiceLineId: z.string().uuid(),
    quantity: z.coerce.number().int().min(1, 'Return at least one').max(100_000),
});
export const creditNoteInputSchema = z.object({
    invoiceId: z.string().uuid(),
    issueDate: z.string().date(),
    reason: z.string().trim().min(2, 'Say why').max(500),
    notes: optionalText,
    lines: z.array(creditNoteLineInputSchema)
        .min(1, 'Return at least one line')
        .refine((lines) => new Set(lines.map((l) => l.invoiceLineId)).size === lines.length, 'Each line once'),
});
/**
 * Correcting a credit note already issued.
 *
 * Lines, invoice and currency are structurally ABSENT: an issued credit note's
 * lines never change (§16). A wrong one is removed and issued again.
 */
export const creditNoteUpdateSchema = patchSchemaOf(z.object({
    issueDate: z.string().date(),
    reason: z.string().trim().min(2, 'Say why').max(500),
    notes: optionalText,
}));
/**
 * Money paid back to a client against a credit note.
 *
 * `currency` must be the credit note's — which is the invoice's. The server
 * refuses anything else, for the reason a payment must match its invoice:
 * subtracting lira from dollars gives a number owed in no currency (rule 5).
 */
export const refundInputSchema = z.object({
    amountMinor: z.coerce.number().int().positive(),
    currency: z.enum(CURRENCIES),
    method: z.enum(PAYMENT_METHODS).default('bank_transfer'),
    refundedOn: z.string().date(),
    reference: optionalText,
    notes: optionalText,
});
/** Correcting a refund. Its currency and credit note cannot change. */
export const refundUpdateSchema = patchSchemaOf(refundInputSchema.omit({ currency: true }));
/** GET /credit-notes — the shared pagination plus the module's own filters. */
export const creditNoteListQuerySchema = paginationSchema.extend({
    invoiceId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    status: z.enum(CREDIT_NOTE_STATUSES).optional(),
});
//# sourceMappingURL=creditNote.js.map